
import {computed, defineAsyncComponent, defineComponent, h, ref} from "vue";
import OrderableAdvancedTable from "@/views/core/components/OrderableAdvancedTable.vue";
import ShareFilterPrompt from "@/components/filters/ShareFilterPrompt.vue";
import SaveFilterPrompt from "@/components/filters/SaveFilterPrompt.vue";
import CheckboxInput from "@/views/core/components/inputs/CheckboxInput.vue";
import BFormSelectOption from "@/components/bootstrap-library/BFormSelectOption.vue";
import BButton from "@/components/bootstrap-library/BButton.vue";
import TextInput from "@/views/core/components/inputs/TextInput.vue";
import SelectInput from "@/views/core/components/inputs/SelectInput.vue";
import ComboBoxInput from "@/views/core/components/inputs/ComboBoxInput.vue";
import BModal from "@/components/bootstrap-library/BModal.vue";
import SearchService from "@/service/SearchService";
import rootStore from "@/store/RootStore";
import {useFilterSearch} from "@/components/filters/UseFilterSearch";
import ProposalSearchResultDTO from "@/dto/ProposalSearchResultDTO";
import ProposalSearchFilter from "@/components/filters/ProposalSearchFilter";
import {TableDefinition} from "@/types";
import WarningIcon from "@/components/WarningIcon.vue";
import {GetResultSetWithCountDTO} from "@/service/api/types/ApiResponseTypes";
import Proposal from "@/domain/proposal/Proposal";
import SaveableSearch from "@/dto/SaveableSearchDTO";
import router from "@/router";

export default defineComponent({
  name: "AdvancedSearch",
  props: {
    actionName: {
      type: String,
      default: 'Select'
    }
  },
  components: {
    OrderableAdvancedTable,
    ShareFilterPrompt,
    SaveFilterPrompt, CheckboxInput, BFormSelectOption, BButton, TextInput, SelectInput, ComboBoxInput, BModal},
  emits: ['select'],
  setup(props, context) {
    const showSavePrompt = ref(false);
    const showSharePrompt = ref(false);
    const searchService = new SearchService();
    const busy = ref(false);

    const profileStore = rootStore.getInstance().profileStore;
    const appConfigStore = rootStore.getInstance().appConfigStore;
    const version = appConfigStore.getVersion();

    const asyncAdvancedFilter = defineAsyncComponent(() =>
        import('@/components/filters/ProposalAdvancedFilters.vue'));

    const {
      filterState, isFilterDefault, isFilterDirty, reset, state: filterSearchState, submit: filterSearch, recordCountWarning,
    } = useFilterSearch<ProposalSearchResultDTO, ProposalSearchFilter>({
      tableKey: 'ProposalData',
      defaultFilters: new ProposalSearchFilter(),
      searchFunction: async (filter: ProposalSearchFilter) => await proposalSearch(filter),
    });

    function handleOpenSavePrompt() {
      showSavePrompt.value = true;
    }

    function handleOpenSharePrompt() {
      showSharePrompt.value = true;
    }

    function hideSharePrompt() {
      showSharePrompt.value = false;
    }

    const table = computed((): TableDefinition<ProposalSearchResultDTO> => ({
          items: filterSearchState.resultSet,
          key: 'ProposalData',
          name: 'Search Proposals',
          columnDefinition: [
            {key: 'proposalNumber', label: 'Proposal Number', searchable: true},
            {key: 'proposalStatus', label: 'Status', searchable: true},
            {key: 'reasonCode', label: 'Reason Code', searchable: true},
            {key: 'dateRequested', label: 'Date Requested', searchable: true},
            {key: 'productNumber', label: 'Product Number', searchable: true},
            {key: 'customerName', label: 'Customer Name', searchable: true},
            {key: 'customerPartDesc', label: 'Customer Part Description', searchable: true},
            {key: 'designer', label: 'Designer', searchable: true},
            {key: 'estimator', label: 'Estimator', searchable: true},
            {key: 'attentionRequiredBy', label: 'Action Required By', searchable: true},
            {key: 'sampleLocation', label: 'Sample Location', searchable: true},
            {key: 'sampleStatus', label: 'Sample Status', searchable: true},
            {key: 'holdCode', label: 'Proposal Hold', searchable: true},
            {key: 'daysOpen', label: 'Days Open', hidden: true},
            {key: 'productRev', label: 'Product Rev', hidden: true},
            {key: 'estimatorDueDate', label: 'Estimator Due Date', hidden: true},
            {key: 'designerDueDate', label: 'Designer Due Date', hidden: true},
            {key: 'autoBuild', label: 'AutoBuild', hidden: true},
            {key: 'containerId', label: 'Container', hidden: true},
            {key: 'sampleRequired', label: 'Sample Req', hidden: true},
            {key: 'promisedDate', label: 'Promise Date', hidden: true},
            {key: 'plant', label: 'Plant', hidden: true},
            {key: 'fobPlant', label: 'FOB Plant', hidden: true},
            {key: 'designReleased', label: 'Design Released', hidden: true},
            {key: 'customerOrderReceived', label: 'Customer Order Recd', hidden: true},
            {key: 'designComplete', label: 'Design Complete', hidden: true},
            {key: 'releasedToProd', label: 'Released To Production', hidden: true},
            {key: 'outsideProcess', label: 'Outside Process', hidden: true},
            {key: 'urgencyCode', label: 'Urgency', hidden: true},
            {key: 'alternateOwner', label: 'Sales Engineer', hidden: true},
            {key: 'opportunityOwner', label: 'Ram/Sam', hidden: true},
            {key: 'sampleRequestDate', label: 'Sample Request Date', hidden: true},
            {key: 'qty1', label: 'Qty 1', hidden: true},
            {key: 'qty2', label: 'Qty 2', hidden: true},
            {key: 'qty3', label: 'Qty 3', hidden: true},
            {key: 'qty4', label: 'Qty 4', hidden: true},
            {key: 'qty5', label: 'Qty 5', hidden: true},
            {key: 'cost1', label: 'Cost 1', hidden: true},
            {key: 'cost2', label: 'Cost 2', hidden: true},
            {key: 'cost3', label: 'Cost 3', hidden: true},
            {key: 'cost4', label: 'Cost 4', hidden: true},
            {key: 'cost5', label: 'Cost 5', hidden: true},
            {key: 'proposedTargetPriceQty1', label: 'Proposed Target Price Qty 1', hidden: true},
            {key: 'proposedTargetPriceQty2', label: 'Proposed Target Price Qty 2', hidden: true},
            {key: 'proposedTargetPriceQty3', label: 'Proposed Target Price Qty 3', hidden: true},
            {key: 'proposedTargetPriceQty4', label: 'Proposed Target Price Qty 4', hidden: true},
            {key: 'proposedTargetPriceQty5', label: 'Proposed Target Price Qty 5', hidden: true},
            {key: 'proposedTargetMarginQty1', label: 'Proposed Target Margin Qty 1', hidden: true},
            {key: 'proposedTargetMarginQty2', label: 'Proposed Target Margin Qty 2', hidden: true},
            {key: 'proposedTargetMarginQty3', label: 'Proposed Target Margin Qty 3', hidden: true},
            {key: 'proposedTargetMarginQty4', label: 'Proposed Target Margin Qty 4', hidden: true},
            {key: 'proposedTargetMarginQty5', label: 'Proposed Target Margin Qty 5', hidden: true},
            {key: 'salesMajorOpportunity', label: 'Sales Major Opp', hidden:true},
            {key: 'projectManager', label: 'Project Manager', searchable: true},
            {key: 'customerPartNum', label: 'Customer Part Num', searchable: true},
            {key: 'validTo', label: 'Valid To', searchable: true},
            {key: 'validFrom', label: 'Valid From', searchable: true}
          ],
          headIcon: () => h(WarningIcon, {
            message: recordCountWarning.value,
          }),
          sidePane: () => h(asyncAdvancedFilter, {
            onSubmit: filterSearch,
            onReset: reset,
            onOpenSavePrompt: handleOpenSavePrompt,
            onOpenSharePrompt: handleOpenSharePrompt,
            modelValue: filterState.value,
            loading: busy.value,
            filterDirty: isFilterDirty.value,
            filterDefault: isFilterDefault.value
          }),
        }),
    )

    // Function provided to useFilterSearch that determines search behavior.
    async function proposalSearch(filter: ProposalSearchFilter): Promise<GetResultSetWithCountDTO<ProposalSearchResultDTO>> {
      if (isFilterDefault.value || !filter) {
        //cannot search unless filter criteria exists
        return { success: true, resultSet: [], totalMatchedRecords: 0 };
      } else {
        return await searchService.getProposalSearchResults(filter);
      }
    }

    function emitSelection(proposal: Proposal) {
      context.emit('select', proposal);
    }

    function openCopyTool(proposal: ProposalSearchResultDTO) {
      /*
      const routeData = router.resolve(
          {
            name: 'CopyProposal',
            params: {initProposalId: proposal}
          })
       */
      const routeData = router.resolve(
          {
            name: 'LegacyCopy',
            params: {proposalNumber: proposal.proposalNumber, productNumber: proposal.productNumber}
          });
      window.open(routeData.href, '_blank')
    }

    async function saveSearch(saveableSearch: SaveableSearch) {
      const searchID = await searchService.saveProposalSearch(saveableSearch.toDTO());
      if(searchID > 0 ) {
        if(saveableSearch.searchID == 0) {
          saveableSearch.searchID = searchID;
          profileStore.ownSearches.push(saveableSearch); // New search was created, update locally with new ID from DB and put in store.
        }
        showSavePrompt.value = false;
      }
    }

    async function shareSearch(saveableSearch: SaveableSearch) {
      const success = await searchService.updateSearchSharePermissions(saveableSearch);
      if(success) {
        hideSharePrompt();
        const search = profileStore.ownSearches.find((s) => {return s.searchID == saveableSearch.searchID});
        if(search) {
          search.sharedWith = saveableSearch.sharedWith; // On success, update local
        }
      }
    }

    return {
      version,
      table,
      emitSelection,
      filterState,
      filterSearchState,
      showSavePrompt,
      showSharePrompt,
      hideSharePrompt,
      saveSearch,
      shareSearch,
      openCopyTool
    }
  }
})
